import { all, call, put, takeEvery, select, delay } from 'redux-saga/effects'
import i18next from 'i18next'
import { actions as chatActions } from './ChatSlice'
import { notificationsEnqueue } from 'src/features/notifications/NotificationsSlice'
import moment from 'moment'
import { getMessages, getLinkedPromptId } from './ChatSelector'
import { actions as loadingActions } from '../loading/LoadingSlice'
import { piwikSiteId } from 'src/constants'
import { reauthAndRetryCall } from 'src/utils/sagaHelpers'
import {
  getPaywallVisible,
  updatePaywallVisible
} from 'src/features/paywall/PaywallSlice'
import { LOCATION_CHANGE } from 'connected-react-router'
import { LLM_MODEL_TYPE } from 'src/services/dougall/constants'
import { getUserRole } from 'src/features/userRole/UserRoleSelector'
import { getRoleSettingsByKey } from 'src/features/config/ConfigSelector'

function* promptRequest(services, { payload }) {
  const DougallService = services('DougallService')
  const SessionService = services('SessionService')
  const Piwik = services('Piwik')
  const messages = yield select(getMessages())
  const date = moment().format('DD/MM/YYYY')

  const haystackTrackingKey = 'fw-news-and-dougall'

  const deviceId = yield call([SessionService, 'getCookie'], 'V2_PSL_DEVICE_ID')

  try {
    const roleKey = yield select(getUserRole())
    const settings = yield select(getRoleSettingsByKey(roleKey))
    const fwAiSettings = settings?.features?.fw_ai || {}

    const maxPrompts = fwAiSettings.max_prompts
    let promptsRequested = yield call(
      [SessionService, 'getFromCache'],
      'promptsRequested',
      0
    )
    const lastPromptDate = yield call(
      [SessionService, 'getFromCache'],
      'lastPromptDate',
      null
    )
    const now = moment()

    const isAnonymous = roleKey === 'ANON'

    if (lastPromptDate && now.diff(moment(lastPromptDate), 'months') >= 1) {
      promptsRequested = 0
      yield call([SessionService, 'saveToCache'], 'promptsRequested', 0)
    }
    if (
      payload?.isPrepromptValid === false &&
      maxPrompts !== null &&
      promptsRequested >= maxPrompts
    ) {
      yield put(
        updatePaywallVisible({
          visible: true,
          type: 'ai_paywall',
          text: isAnonymous
            ? `This is an exclusive feature. Register for expanded access.`
            : `You've reached your monthly limit of prompts. Contact us to unlock unlimited access.`
        })
      )
      yield put(chatActions.promptFail())
      return
    }
    // Add date chunk divider
    if (date && !messages.find(m => m.date === date && m.isDivider)) {
      yield put(
        chatActions.addMessage({
          date,
          isDivider: true
        })
      )
    }
    // Update Chat reducer
    yield put(
      chatActions.addMessage({
        message: payload.hcpPrompt,
        isAnswer: false,
        date: moment().format()
      })
    )
    yield put(
      chatActions.setFormState({
        prompt: ''
      })
    )

    // Exit if user is anon & deviceId is not ready
    yield put(chatActions.promptRequestStart())

    const dspId = ''
    const [languageCode, countryCode] = navigator.language.split('-')
    const source = parseInt(piwikSiteId)
    const linkedPromptId = yield select(getLinkedPromptId()) || null

    if (linkedPromptId) {
      payload.applicationLinkedPromptId = linkedPromptId
    }

    const response = yield reauthAndRetryCall(
      services,
      [DougallService, 'promptStreamRequest'],
      {
        ...payload,
        countryCode: countryCode || 'US',
        languageCode: languageCode || 'en',
        dspId,
        type: LLM_MODEL_TYPE,
        source,
        deviceId
      }
    )

    if (response) {
      if (maxPrompts !== null) {
        if (!payload?.isPrepromptValid) {
          yield call(
            [SessionService, 'saveToCache'],
            'promptsRequested',
            promptsRequested + 1
          )
          yield call(
            [SessionService, 'saveToCache'],
            'lastPromptDate',
            now.format()
          )
        }
      }
    }
  } catch (e) {
    yield call(
      [Piwik, 'track'],
      'firstword-ai',
      'failed',
      `prompt-answer_${haystackTrackingKey}`
    )

    console.log(e)
    // Handle Timeout error & add error answer
    if ([408, 504].includes(e.getCode())) {
      yield put(
        chatActions.addMessage({
          answer: i18next.t('dougallGptTimeoutAnswer'),
          isAnswer: true,
          showSocial: false,
          isError: true,
          showWarning: false,
          date: moment().format()
        })
      )
      yield put(chatActions.promptFail())
    } else {
      yield put(
        notificationsEnqueue({
          message: 'There was a problem with the request.',
          type: 'error'
        })
      )
      yield put(chatActions.promptFail())
    }
  } finally {
    // Reset stored path to avoid multiple url prompts requests
    yield put(loadingActions.pathReset())
    // Set URL flag request to true
    yield put(chatActions.setUrlRequested(true))
  }
}

function* actionSubmit(services, { payload }) {
  const DougallService = services('DougallService')
  const Piwik = services('Piwik')
  const Session = services('Session')

  try {
    const { action, promptId, answerId, feedback, undo, parentPromptId } =
      payload

    const deviceId = yield call([Session, 'getCookie'], 'PSL_DEVICE_ID')

    const profile = yield call([Session, 'userLoad'])

    const source = parseInt(piwikSiteId)

    const { actionId } = yield call([DougallService, 'answerActionRequest'], {
      action,
      applicationPromptId: promptId,
      answerId,
      applicationLinkedPromptId: parentPromptId,
      deviceId,
      undo,
      source,
      emailId: profile?.party_email_id
    })

    if (actionId) {
      if (feedback) {
        const response = yield call([DougallService, 'answerFeedbackSubmit'], {
          feedback,
          actionId,
          source,
          emailId: profile?.party_email_id
        })

        if (response) {
          yield put(
            notificationsEnqueue({
              message: i18next.t('feedbackThankYou'),
              type: 'success'
            })
          )
          yield call(
            [Piwik, 'trackAction'],
            'Site/View/Home/feedback-overlay/success',
            {}
          )
        } else {
          yield put(
            notificationsEnqueue({
              message: response,
              type: 'error'
            })
          )
        }
      }
    } else {
      yield put(
        notificationsEnqueue({
          message: 'There was a problem submitting your action',
          type: 'error'
        })
      )
    }
  } catch (e) {
    console.error(e)
    yield put(
      notificationsEnqueue({
        message: 'There was a problem submitting your action',
        type: 'error'
      })
    )
  }
}

function* shareEmail(services, { payload }) {
  const DougallService = services('DougallService')
  const UserTransactionService = services('UserTransactionService')

  try {
    const {
      pId = undefined // If pId then is Email Share
    } = payload

    const service = pId
      ? [DougallService, 'promptShareRequest'] // Share prompt
      : [UserTransactionService, 'inviteSubmit'] // Invite your colleague
    const response = yield call(service, payload)

    if (response) {
      yield put(
        notificationsEnqueue({
          message: i18next.t('emailShareFormSnackbarMessage'),
          type: 'success'
        })
      )
    }
  } catch (e) {
    console.error(e)
    yield put(
      notificationsEnqueue({
        message: i18next.t('commentsSagaErrorMessage'),
        type: 'error'
      })
    )
  }
}

function* trackRiverPageView(services, action) {
  const {
    payload: {
      location: { pathname }
    }
  } = action

  if (pathname.match(/^\/firstword-ai/)) {
    try {
      const Piwik = services('Piwik')

      yield delay(0)
      const isPaywallVisible = yield select(getPaywallVisible)

      const actionType = isPaywallVisible
        ? 'firstword-ai_paywall'
        : pathname?.slice(1)

      yield call([Piwik, 'track'], 'page', 'view', actionType)
    } catch (error) {
      console.log(error)
    }
  }
}

export default function* watchChat(services) {
  yield all([
    takeEvery(chatActions.promptRequest, promptRequest, services),
    takeEvery(chatActions.actionSubmit, actionSubmit, services),
    takeEvery(chatActions.shareEmail, shareEmail, services),
    takeEvery(LOCATION_CHANGE, trackRiverPageView, services)
  ])
}
